import React, { useEffect, useState } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "./../../graphics/logo.svg";
import { Layout } from "antd";
import { useSelector } from "react-redux";
import { AppRoutes } from "../../data";
import DashboardPage from "../../pages/DashboardPage";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import "./layout.css";
import TopNav from "./TopNav";
import { setIsShowTableInDesktop } from "../../redux-toolkit/slices/generalSlice";
const { Content } = Layout;

const AppLayout = () => {
  const dispatch = useAppDispatch();

  const [routes, setRoutes] = useState<IRoute[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { meta } = useSelector((state: RootState) => state.chat);

  const updateScreenSize = () => {
    const isDesktop = window.innerWidth > 1000;
    dispatch(setIsShowTableInDesktop(isDesktop));
  };

  useEffect(() => {
    // Initial check
    updateScreenSize();

    // Add event listener for resize
    window.addEventListener("resize", updateScreenSize);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("resize", updateScreenSize);
    };
  }, [dispatch]);

  useEffect(() => {
    setRoutes(AppRoutes());
  }, []);

  const location = useLocation();

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".navbar-dropdown") &&
        !target.closest(".navbar-user-avatar")
      ) {
        setDropdownOpen(false);
      }
      if (
        !target.closest(".navbar-mobile-toggle") &&
        !target.closest(".navbar-mobile-menu")
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <Layout>
        <nav className="navbar-container">
          <div className="navbar-content">
            {/* Logo Section */}
            <div className="navbar-logo">
              <Link to="/">
                <Logo />
              </Link>
            </div>

            {/* Navigation Menu */}
            <div className="navbar-menu">
              <div className="navbar-menu-container">
                <Link
                  to="/orders"
                  className={`navbar-menu-item ${
                    location.pathname === "/orders" ? "active" : ""
                  }`}
                >
                  My Orders
                </Link>
                <Link
                  to="/orders"
                  className={`navbar-menu-item ${
                    location.pathname === "/orders" ? "active" : ""
                  }`}
                >
                  My Formulas
                </Link>
                <div
                  className={`navbar-dropdown ${dropdownOpen ? "open" : ""}`}
                >
                  <div
                    className="navbar-dropdown-toggle"
                    onClick={toggleDropdown}
                  >
                    <span>Learning Center</span>
                    <span className="navbar-dropdown-arrow">▼</span>
                  </div>
                  <div className="navbar-dropdown-menu">
                    <div className="navbar-dropdown-content">
                      <div className="navbar-dropdown-feature">
                        <div className="navbar-dropdown-feature-badge">
                          <span>🎉</span>
                          <span>Take the quiz!</span>
                        </div>
                        <h4>How to use your cream correctly.</h4>
                        <p>
                          Find out instantly if our personalized topical creams
                          can target your specific pain.
                        </p>
                        <Link to="/order-cream" className="btn btn-primary">
                          Learn More
                        </Link>
                      </div>
                      <div className="navbar-dropdown-links">
                        <div className="navbar-dropdown-section">
                          <h5>Understanding Your Pain</h5>
                          <ul className="navbar-dropdown-links-list">
                            <li>
                              <Link to="/learn/how-to-use-meter-dose-applicator">
                                <span>
                                  How to use your metered dose applicator
                                </span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/learn/how-compounded-creams-work">
                                <span>How compounded creams work</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/learn/what-is-telehealth">
                                <span>What is telehealth?</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/learn/condition-guides">
                                <span>Condition Guides</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <div className="navbar-dropdown-section">
                          <h5>Tools & Resources</h5>
                          <ul className="navbar-dropdown-links-list">
                            <li>
                              <Link to="/learn">
                                <span>Learning Center</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/refills">
                                <span>Refills & Formula Changes</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/faq">
                                <span>FAQs</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/testimonials">
                                <span>Patient Testimonials</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <div className="navbar-dropdown-section">
                          <h5>Get Started</h5>
                          <ul className="navbar-dropdown-links-list">
                            <li>
                              <Link to="/how-it-works">
                                <span>How it Works</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/pricing">
                                <span>Insurance & Pricing</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                            <li>
                              <Link to="/about-us">
                                <span>About Us</span>
                                <span className="arrow-icon">↗</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="navbar-actions">
              <Link to="/payment" className="navbar-order-btn">
                Order Now
              </Link>
              <TopNav />
            </div>

            {/* Mobile Menu Toggle */}
            <button className="navbar-mobile-toggle" onClick={toggleMobileMenu}>
              <span className="icon">☰</span>
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`navbar-mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
            <div className="navbar-mobile-menu-content">
              <div className="navbar-mobile-menu-items">
                <Link
                  to="/orders"
                  className={`navbar-mobile-menu-item ${
                    location.pathname === "/orders" ? "active" : ""
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Orders
                </Link>
                <Link
                  to="/orders"
                  className={`navbar-mobile-menu-item ${
                    location.pathname === "/orders" ? "active" : ""
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Formulas
                </Link>
                <Link
                  to="/learn"
                  className={`navbar-mobile-menu-item ${
                    location.pathname === "/learn" ? "active" : ""
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Learning Center
                </Link>
              </div>
              <Link
                to="/payment"
                className="navbar-mobile-order-btn"
                onClick={() => setMobileMenuOpen(false)}
              >
                Order Now
              </Link>
            </div>
          </div>
        </nav>

        <Content className="main-content-wrapper">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            {routes.map((route: any) => {
              return (
                <Route
                  path={route.path}
                  key={Math.random()}
                  element={<route.Component />}
                />
              );
            })}
          </Routes>
        </Content>
      </Layout>
    </>
  );
};

export default AppLayout;
